import { meQueryOptions } from "@/api/auth";
import { directInvitationDetailsQuery } from "@/api/directInvitation";
import { invitationDetailsQuery } from "@/api/invitation";
import { myWalletQueryOptions } from "@/api/me";
import { api, logout } from "@/api/utils";
import { PageInset } from "@/components/PageInset";
import { Button } from "@/components/ui/Button";
import { IconButtonLink } from "@/components/ui/Link";
import { Box, Flex, QueryHandler, Text } from "@givenwell/components";
import { ChevronLeftIcon, LinkIcon } from "@givenwell/core-solid-icons";
import { GetDirectInvitationDetailsResponse, GetInvitationDetailsResponse } from "@givenwell/marketplace-api";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, Navigate, useNavigate } from "@tanstack/react-router";
import { fallback, object, optional, picklist, string } from "valibot";

export const Route = createFileRoute("/_auth/merge-account")({
  component: InvitationMergeController,
  validateSearch: object({
    token: fallback(optional(string(), ""), ""),
    kind: picklist(["supporter", "direct"]),
  }),
});

const route = getRouteApi("/_auth/merge-account");

type InvitationKind = "supporter" | "direct";

function useDetailsQuery(kind: InvitationKind, token: string) {
  const direct = useQuery({ ...directInvitationDetailsQuery(token), enabled: kind === "direct" && !!token });
  const supporter = useQuery({ ...invitationDetailsQuery(token), enabled: kind === "supporter" && !!token });
  return (kind === "direct" ? direct : supporter) as UseQueryResult<
    GetDirectInvitationDetailsResponse | GetInvitationDetailsResponse,
    Error
  >;
}

function InvitationMergeController() {
  const { token, kind } = route.useSearch();
  const detailsQuery = useDetailsQuery(kind, token);

  if (token) {
    if (kind === "supporter") {
      return (
        <InvitationMerge
          token={token}
          kind="supporter"
          supporterName={(detailsQuery.data as GetInvitationDetailsResponse)?.supporter_display_name || "unknown"}
        />
      );
    } else {
      return <InvitationMerge token={token} kind="direct" />;
    }
  }

  return <Navigate to="/" />;
}

function useAcceptSupporterInvitationMutation(token: string) {
  return useMutation({
    mutationFn: async () => {
      await api.invitation.acceptInvitationWithMerge({ invitationToken: token });
    },
  });
}

function useAcceptDirectInvitationMutation(token: string) {
  return useMutation({
    mutationFn: async () => {
      await api.directInvitation.acceptDirectInvitationWithMerge({ invitationToken: token });
    },
  });
}

type InvitationMergeProps = { token: string; kind: InvitationKind; supporterName?: string };
function InvitationMerge({ token, kind, supporterName }: InvitationMergeProps) {
  const meQuery = useQuery(meQueryOptions);
  const navigate = useNavigate();
  const client = useQueryClient();

  const acceptMut = useAcceptSupporterInvitationMutation(token);
  const acceptDirectMut = useAcceptDirectInvitationMutation(token);
  const mut = kind === "direct" ? acceptDirectMut : acceptMut;

  function handleAccept() {
    mut.mutate(undefined, {
      onSuccess() {
        client.invalidateQueries(myWalletQueryOptions);
        navigate({ to: "/marketplace" });
      },
    });
  }

  return (
    <PageInset css={{ minHeight: "100%", maxW: 600, mx: "auto" }}>
      <Box css={{ bg: colors.cream, pt: "var(--safe-area-inset-top)" }}>
        <Box css={{ h: 64, display: "flex", items: "center" }}>
          <IconButtonLink
            aria-label="Back"
            to="/invitation/activate"
            search={{
              token: token,
            }}
            variant="plain"
            color="neutral"
          >
            <ChevronLeftIcon />
          </IconButtonLink>
        </Box>
      </Box>
      <Box css={{ h: 20 }} />

      <Title css={{ mb: 24, fontFamily: fonts.ftHabit }}>Confirmation</Title>
      {kind === "supporter" ? (
        <Description css={{ mb: 20 }}>
          By confirming below, you agree to merge the following accounts. This is private, and neither organisation will
          know about the merge. It's for your convenience, so you only have one account to manage.
        </Description>
      ) : (
        <Description css={{ mb: 20 }}>
          By confirming below, you agree to accept this gift into your existing Givenwell account.
        </Description>
      )}

      <QueryHandler
        query={meQuery}
        success={({ data: me }) => (
          <Box css={{ spaceY: 10 }}>
            {supporterName && (
              <>
                <Box css={{ p: 20, rounded: 8, border: `1px solid ${colors.outline}` }}>
                  <Text css={{ fontScale: "xl", weight: 700, fontFamily: fonts.ftHabit }}>{supporterName}</Text>
                  <Text css={{ fontScale: "sm" }}>Employer</Text>
                </Box>
                <Flex css={{ justifyContent: "center" }}>
                  <LinkIcon width="42" height="42" />
                </Flex>
              </>
            )}

            <Box css={{ p: 20, rounded: 8, border: `1px solid ${colors.outline}` }}>
              <Text css={{ fontScale: "xl", weight: 700, fontFamily: fonts.ftHabit }}>
                {me.given_name} {me.family_name}
              </Text>
              <Text css={{ fontScale: "sm" }}>{me.email}</Text>
            </Box>
          </Box>
        )}
      />
      <Box css={{ flex: "1 0 0px" }} />

      <Button
        onClick={handleAccept}
        variant="filled"
        color="primary"
        size="lg"
        css={{
          w: "100%",
        }}
        loading={mut.isPending}
        loadingText="Please wait..."
      >
        Merge Account
      </Button>
      <Button
        onClick={async () => {
          await logout();
          window.location.reload();
        }}
        variant="tonal"
        color="primary"
        size="lg"
        css={{
          w: "100%",
          mt: 16,
        }}
        disabled={mut.isPending}
      >
        Log Out
      </Button>
      <Box css={{ h: "max(var(--safe-area-inset-bottom), 16px)" }} />
    </PageInset>
  );
}

const Title = styled("h1", {
  fontScale: "3xl",
  color: colors.blue800,
  weight: 500,
});

const Description = styled("p", {
  fontScale: "md",
  color: colors.text2,
});
